import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Plus, Trash2, Youtube, Play, Flame, Search, Megaphone } from "lucide-react";
import { toast } from "sonner";

/**
 * Editor to pin preroll trailers + ads for a showtime. Tabs:
 *  - "Suchen"  → TMDb search by title
 *  - "Trends"  → TMDb top-10 trending
 *  - "Werbespots" → Eigene Ads aus der Kartei einfügen
 */
export default function PrerollEditor({ showtime, onClose, onSaved }) {
  const [rows, setRows] = useState(showtime.preroll_trailers || []);
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewYt, setPreviewYt] = useState(null);
  const [trends, setTrends] = useState([]);
  const [trendWindow, setTrendWindow] = useState("week");
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [ads, setAds] = useState([]);

  useEffect(() => { api.get("/preroll-ads").then((r) => setAds(r.data || [])).catch(() => {}); }, []);

  const pinAd = (a) => {
    if (!a.enabled) return toast.error("Werbespot ist deaktiviert");
    if (rows.some((r) => r.kind === "ad" && r.ad_id === a.id)) return toast.info("Spot bereits eingeplant");
    setRows((r) => [...r, { kind: "ad", ad_id: a.id, title: a.title, poster_url: a.media_url || "", duration_sec: a.duration_sec }]);
    toast.success(`+ ${a.title}`);
  };

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/tmdb/search`, { params: { q } });
      setResults(data.slice(0, 8));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadTrends = async (window = trendWindow) => {
    setTrendsLoading(true);
    try {
      const { data } = await api.get(`/tmdb/trending`, { params: { window } });
      setTrends(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTrendsLoading(false); }
  };

  useEffect(() => { if (tab === "trends" && trends.length === 0) loadTrends(); }, [tab, trends.length]);

  const pinFromTrend = (t) => {
    if (!t.trailer_youtube_id) {
      toast.error("Kein YouTube-Trailer verfügbar");
      return;
    }
    if (rows.some((r) => r.youtube_id === t.trailer_youtube_id)) {
      toast.info("Trailer bereits gepinnt");
      return;
    }
    setRows((r) => [...r, {
      tmdb_id: t.tmdb_id,
      title: t.title,
      youtube_id: t.trailer_youtube_id,
      poster_url: t.poster_url,
    }]);
    toast.success(`„${t.title}" hinzugefügt`);
  };

  const pick = async (tmdbId) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/tmdb/movie/${tmdbId}`);
      const url = data.trailer_url || "";
      let yt = "";
      if (url.includes("watch?v=")) yt = url.split("watch?v=")[1].split("&")[0];
      else if (url.includes("youtu.be/")) yt = url.split("youtu.be/")[1];
      if (!yt) return toast.error("Kein YouTube-Trailer gefunden");
      setRows((r) => [...r, {
        tmdb_id: data.tmdb_id,
        title: data.title_de || data.title_original,
        youtube_id: yt,
        poster_url: data.poster_url,
        duration_min: data.duration_min,
      }]);
      setResults([]);
      setQ("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addManual = () => setRows((r) => [...r, { youtube_id: "", title: "" }]);
  const upd = (i, k, v) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const move = (i, d) => setRows((r) => {
    const arr = [...r];
    const j = i + d;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const save = async () => {
    setBusy(true);
    try {
      // Trailer ohne youtube_id werden verworfen, Ads ohne ad_id auch
      const cleaned = rows.filter((r) => (r.kind === "ad" ? r.ad_id : r.youtube_id));
      await api.patch(`/showtimes/${showtime.id}`, { ...showtime, preroll_trailers: cleaned });
      toast.success("Preroll gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6" data-testid="preroll-editor">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">Trailer-Werbeblock</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-xs text-[#A8A398]">
            Wähle Trailer, die vor <span className="text-[#F5F0E6]">{showtime.movie?.title_de || "diesem Film"}</span> als Werbeblock laufen. Falls keine gesetzt sind, greift die Zufallsauswahl aus TMDb.
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-[#1A1A1A]">
            <button
              data-testid="preroll-tab-search"
              onClick={() => setTab("search")}
              className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 transitions-safe ${
                tab === "search" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A398] hover:text-[#F5F0E6]"
              }`}
            >
              <Search size={12} /> Suchen
            </button>
            <button
              data-testid="preroll-tab-trends"
              onClick={() => setTab("trends")}
              className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 transitions-safe ${
                tab === "trends" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A398] hover:text-[#F5F0E6]"
              }`}
            >
              <Flame size={12} /> Aktuelle Trends
            </button>
            <button
              data-testid="preroll-tab-ads"
              onClick={() => setTab("ads")}
              className={`px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 transitions-safe ${
                tab === "ads" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A398] hover:text-[#F5F0E6]"
              }`}
            >
              <Megaphone size={12} /> Werbespots
            </button>
          </div>

          {tab === "search" && (
            <div className="card-lux p-3">
              <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="TMDb-Titel suchen …" className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
                <button data-testid="preroll-search-btn" onClick={search} disabled={busy} className="bg-[#D4AF37] text-black px-4 py-2 rounded text-sm uppercase tracking-widest">Suchen</button>
                <button onClick={addManual} className="border border-[#2A2A2A] px-3 py-2 rounded text-xs text-[#A8A398] flex items-center gap-1"><Plus size={12} /> Manuell</button>
              </div>
              {results.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {results.map((r) => (
                    <button key={r.tmdb_id} onClick={() => pick(r.tmdb_id)} className="text-left bg-[#050505] border border-[#2A2A2A] rounded p-2 hover:border-[#D4AF37] transitions-safe">
                      <div className="aspect-[2/3] bg-[#1A1A1A] mb-1 rounded overflow-hidden">{r.poster_url && <img src={r.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}</div>
                      <div className="text-xs truncate">{r.title}</div>
                      <div className="text-[10px] text-[#A8A398]">{r.year}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "trends" && (
            <div className="card-lux p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-2">
                  <Flame size={12} className="text-[#D4AF37]" /> Top 10 · TMDb Trends
                </div>
                <div className="flex gap-1">
                  {[["day","Heute"],["week","Woche"]].map(([v,l]) => (
                    <button key={v}
                      data-testid={`preroll-trend-${v}`}
                      onClick={() => { setTrendWindow(v); loadTrends(v); }}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border transitions-safe ${
                        trendWindow === v ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:text-[#F5F0E6]"
                      }`}>{l}</button>
                  ))}
                </div>
              </div>
              {trendsLoading ? (
                <div className="text-center text-xs text-[#A8A398] py-8">Lade Trends …</div>
              ) : trends.length === 0 ? (
                <div className="text-center text-xs text-[#A8A398] py-8">Keine Trends verfügbar. Ist TMDB_API_KEY gesetzt?</div>
              ) : (
                <div className="grid grid-cols-5 gap-2">
                  {trends.map((t, idx) => {
                    const already = rows.some((r) => r.youtube_id === t.trailer_youtube_id);
                    return (
                      <button
                        key={t.tmdb_id}
                        data-testid={`preroll-trend-pick-${idx}`}
                        onClick={() => pinFromTrend(t)}
                        disabled={!t.trailer_youtube_id}
                        className={`relative text-left bg-[#050505] border rounded p-1.5 transitions-safe ${
                          already ? "border-[#D4AF37]/40 opacity-60" :
                          t.trailer_youtube_id ? "border-[#2A2A2A] hover:border-[#D4AF37] hover:scale-[1.02]" : "border-[#2A2A2A] opacity-40 cursor-not-allowed"
                        }`}
                        title={t.trailer_youtube_id ? "Klicken zum Pinnen" : "Kein YouTube-Trailer"}
                      >
                        <div className="absolute top-1 left-1 z-10 bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono px-1 rounded">#{idx + 1}</div>
                        {t.trailer_youtube_id && (
                          <div className="absolute top-1 right-1 z-10 bg-[#D4AF37] text-black text-[9px] uppercase px-1 rounded flex items-center gap-0.5">
                            <Play size={8} fill="currentColor" />
                          </div>
                        )}
                        <div className="aspect-[2/3] bg-[#1A1A1A] rounded overflow-hidden mb-1">
                          {t.poster_url && <img src={t.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />}
                        </div>
                        <div className="text-[11px] truncate leading-tight">{t.title}</div>
                        <div className="text-[9px] text-[#A8A398] flex items-center gap-1">
                          {t.year} · ★ {t.vote_average?.toFixed?.(1) || "—"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="text-[10px] text-[#A8A398] mt-3">Klick pinnt den Trailer sofort.</div>
            </div>
          )}

          {tab === "ads" && (
            <div className="card-lux p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-2">
                  <Megaphone size={12} className="text-[#D4AF37]" /> Werbespots · {ads.length}
                </div>
                <a href="/admin/werbung" target="_blank" rel="noreferrer" className="text-[10px] text-[#D4AF37] hover:text-[#EEDC82]">+ Neuer Spot</a>
              </div>
              {ads.length === 0 ? (
                <div className="text-center text-xs text-[#A8A398] py-6">Noch keine Werbespots angelegt. Öffne „Werbespots" im Menü.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ads.map((a) => {
                    const already = rows.some((r) => r.kind === "ad" && r.ad_id === a.id);
                    return (
                      <button key={a.id} onClick={() => pinAd(a)} disabled={!a.enabled}
                        data-testid={`preroll-ad-pin-${a.id}`}
                        className={`relative flex items-center gap-2 p-2 border rounded text-left transitions-safe ${
                          already ? "border-[#D4AF37]/40 opacity-60"
                          : a.enabled ? "border-[#2A2A2A] hover:border-[#D4AF37]"
                          : "border-[#2A2A2A] opacity-40 cursor-not-allowed"
                        }`}>
                        {a.media_url ? <img src={a.media_url} alt="" className="w-12 h-12 object-cover rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="w-12 h-12 rounded flex items-center justify-center" style={{ background: a.background_color || "#0A0A0A" }}><Megaphone size={18} style={{ color: a.accent_color || "#D4AF37" }} /></div>}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs truncate">{a.title}</div>
                          <div className="text-[10px] text-[#A8A398] truncate">{a.subtitle || `${a.duration_sec}s`}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Reihenfolge · {rows.length} Trailer</div>
            {rows.map((r, i) => {
              const isAd = r.kind === "ad";
              return (
              <div key={i} className={`flex items-center gap-2 border rounded p-2 ${isAd ? "border-[#8B7328]/50 bg-[#8B7328]/[0.06]" : "border-[#2A2A2A] bg-[#050505]"}`}>
                <div className="text-[#A8A398] font-mono text-xs w-6">{i + 1}.</div>
                {isAd ? (
                  r.poster_url ? <img src={r.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-8 h-12 object-cover rounded" /> : <div className="w-8 h-12 bg-[#141414] rounded flex items-center justify-center text-[#EEDC82]"><Megaphone size={14} /></div>
                ) : (
                  r.poster_url ? <img src={r.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-8 h-12 object-cover rounded" /> : <div className="w-8 h-12 bg-[#1A1A1A] rounded flex items-center justify-center text-[#A8A398]"><Youtube size={14} /></div>
                )}
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input value={r.title || ""} onChange={(e) => upd(i, "title", e.target.value)} placeholder={isAd ? "Spot-Titel" : "Titel"} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-sm outline-none focus:border-[#D4AF37]" />
                  {isAd ? (
                    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono text-[#EEDC82] flex items-center gap-1"><Megaphone size={11} /> WERBUNG · {r.duration_sec || 5}s</div>
                  ) : (
                    <input value={r.youtube_id || ""} onChange={(e) => upd(i, "youtube_id", e.target.value)} placeholder="YouTube-ID" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-sm font-mono outline-none focus:border-[#D4AF37]" />
                  )}
                </div>
                {!isAd && (
                  <button
                    data-testid={`preroll-play-${i}`}
                    onClick={() => r.youtube_id && setPreviewYt({ id: r.youtube_id, title: r.title || "Trailer" })}
                    disabled={!r.youtube_id}
                    className="text-[#D4AF37] px-1 disabled:opacity-30 hover:scale-110 transitions-safe"
                    title="Vorschau"
                  >
                    <Play size={16} fill="currentColor" />
                  </button>
                )}
                <button onClick={() => move(i, -1)} className="text-[#A8A398] px-1">↑</button>
                <button onClick={() => move(i, 1)} className="text-[#A8A398] px-1">↓</button>
                <button onClick={() => remove(i)} className="text-[#EE9CA1] px-1"><Trash2 size={14} /></button>
              </div>
            );})}
            {!rows.length && <div className="text-center text-xs text-[#A8A398] py-6">Keine Trailer gepinnt — es wird Zufallsauswahl aus TMDb verwendet.</div>}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="preroll-save-btn" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">Speichern</button>
        </div>
      </div>
      {previewYt && (
        <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur flex items-center justify-center p-6" onClick={() => setPreviewYt(null)}>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Vorschau · {previewYt.title}</div>
              <button onClick={() => setPreviewYt(null)} className="text-[#A8A398] hover:text-[#F5F0E6]"><X /></button>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden border border-[#2A2A2A]">
              <iframe
                title="preview"
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${previewYt.id}?autoplay=1&modestbranding=1&rel=0`}
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
